import { domParserConstructor } from "./util/crossplatform";

interface CustomImage {
    height: number;
    naturalHeight: number;
    naturalWidth: number;
    src: string | ArrayBuffer;
    width: number;
}

interface CustomContext {
    drawImage(image: CustomCanvas | CustomImage, dx: number, dy: number): void;
    drawImage(image: CustomCanvas | CustomImage, dx: number, dy: number, dw: number, dh: number): void;
    drawImage(
        image: CustomCanvas | CustomImage,
        sx: number,
        sy: number,
        sw: number,
        sh: number,
        dx: number,
        dy: number,
        dw: number,
        dh: number,
    ): void;
    rotate(angle: number): void;
    scale(x: number, y: number): void;
    translate(x: number, y: number): void;
}

interface CustomCanvas {
    height: number;
    getContext(type: string): CustomContext | null;
    toDataURL(): string;
    toDataURL(type?: string, quality?: number): string;
    width: number;
}

let createCanvasInternal: ((width?: number, height?: number, type?: "pdf" | "svg") => CustomCanvas) | undefined;
let loadImageInternal: ((src: string | ArrayBuffer, allowCrossOrigin?: boolean) => Promise<CustomImage>) | undefined;

const createCanvas = (width?: number, height?: number, type?: "pdf" | "svg"): CustomCanvas => {
    if (createCanvasInternal !== undefined) {
        return createCanvasInternal(width, height, type);
    }
    try {
        const module = documentCanvasModule(true);
        setCanvasModule(module!);
        return module!.createCanvas(width, height, type);
    } catch (e) {
        throw new Error("Canvas module not set");
    }
};
const loadImage = (src: string | ArrayBuffer, allowCrossOrigin?: boolean): Promise<CustomImage> => {
    if (loadImageInternal !== undefined) {
        return loadImageInternal(src, allowCrossOrigin);
    }
    try {
        const module = documentCanvasModule(true);
        setCanvasModule(module!);
        return module!.loadImage(src, allowCrossOrigin);
    } catch (e) {
        throw new Error("Canvas module not set");
    }
};

interface CustomCanvasModule {
    createCanvas: typeof createCanvas;
    loadImage: typeof loadImage;
}

function setCanvasModule(module?: CustomCanvasModule) {
    createCanvasInternal = module?.createCanvas;
    loadImageInternal = module?.loadImage;
}

function documentCanvasModule(throwOnError: boolean): CustomCanvasModule | undefined {
    try {
        const ctx = document.createElement("canvas").getContext("2d");
        if (ctx) {
            return {
                createCanvas: (width?: number, height?: number, _type?: "pdf" | "svg") => {
                    const canvas: HTMLCanvasElement = document.createElement("canvas");
                    if (width !== undefined && width !== null) {
                        canvas.width = width;
                    }
                    if (height !== undefined && height !== null) {
                        canvas.height = height;
                    }
                    return canvas as any;
                },
                loadImage: (src: string | ArrayBuffer, allowCrossOrigin?: boolean) => {
                    return new Promise((resolve, reject) => {
                        const image = document.createElement("img");
                        if (allowCrossOrigin !== false) {
                            image.crossOrigin = "Anonymous";
                        }
                        image.onload = () => {
                            resolve(image);
                        };
                        image.onerror = reject;
                        image.src = src as string;
                    });
                },
            };
        }
    } catch (e) {
        if (throwOnError) {
            throw e;
        }
    }
    return undefined;
}

// DOMParser constructor to be used by Pith.from().
let domParser: typeof DOMParser | undefined;
function getDomParser(): typeof DOMParser {
    if (domParser === undefined) {
        domParser = domParserConstructor();
    }
    return domParser;
}
function setDomParser(parser: typeof DOMParser) {
    domParser = parser;
}

export { createCanvas, documentCanvasModule, getDomParser, loadImage, setCanvasModule, setDomParser };
export type { CustomCanvas, CustomContext, CustomImage, CustomCanvasModule };
